<template>
  <f7-popup
    v-model:opened="opened"
    class="change-machine-popup"
    close-on-escape
  >
    <f7-page v-if="opened">
      <template #fixed>
        <TheNavbar
          nav-title="Modificar máquina para el parte"
          class="secondary-navbar"
          :popup-close-btn-enable="true"
        />
      </template>

      <UCPreload
        :show="isLoadingMachines"
        :number="1"
      />

      <TransitionGroup>
        <template v-if="!isLoadingMachines">
          <f7-block>
            <UCSearchEntityERP
              ref="searchMachineRef"
              title="Búsqueda de máquinas"
              placeholder="Buscar…"
              search-text="Buscando máquinas"
              search-cancellation-text="Búsqueda de máquinas cancelada…"
              search-not-found-text="No se han encontrado máquinas"
              :disabled="isModifyingMachine"
              :default-etities-list="machineList"
              :property-entity-shown-input="['Codigo', 'NumeroSerie', 'Nombre']"
              :property-entity-shown-list-title="['Nombre']"
              :property-entity-shown-list-subtitle="['Codigo', 'NumeroSerie', 'NombreAlmacen']"
              :emit-evet-change-default-value="true"
              :autofocus-on-deselection="true"
              :auto-focus-on-empty-search-result="true"
              @selection="handleMachineSelection"
            >
              <template #subtitle-0="{ item }">
                {{ `${$t('common.code')}: ${item}` }}
              </template>
              <template #subtitle-1="{ item }">
                {{ `${$t('common.serialNumber')}: ${item}` }}
              </template>
              <template #subtitle-2="{ item }">
                {{ `${$t('common.warehouse')}: ${item}` }}
              </template>
            </UCSearchEntityERP>
          </f7-block>
          <f7-block>
            <f7-button
              :preloader="isModifyingMachine"
              :loading="isModifyingMachine"
              :disabled="!selectedMachine || isModifyingMachine"
              fill
              color="green"
              @click="handleModificationConfirmation"
            >
              Confirmar cambio de máquina
            </f7-button>
          </f7-block>
        </template>
      </TransitionGroup>
    </f7-page>
  </f7-popup>
</template>

<script setup>
// @ts-check

import {
  nextTick,
  ref,
  shallowRef,
  watch,
} from 'vue';
import { ConfirmDialogGH, HiNotification } from '@/js/common/HINotificationSupport';
import { callFetchMachinesList, callModifyPartMachine } from '@/js/services/repairs/ApiRepairs';
import { logError } from '@/js/utils/log';

import TheNavbar from '@/components/TheNavbar.vue';
import UCPreload from '@/components/common/UCPreload.vue';
import UCSearchEntityERP from '@/components/common/UCSearchEntityERP.vue';

/** @import { Ref, ShallowRef } from 'vue' */

const props = defineProps({
  workPartId: { type: Number, default: undefined },
});

const emit = defineEmits(['machineUpdated']);

const opened = defineModel('opened', {
  type: Boolean,
  default: false,
});

function useMachineLoading() {
  /** Controls whether a preloader is shown during machines loading. */
  const isLoadingMachines = ref(false);

  /**
   * The list of available machines.
   * @type {Ref<Record<string, any>[]>}
   */
  const machineList = ref([]);

  /**
   * Fetches the list of available machines.
   * @returns {Promise<boolean>} True if the operation was successful, false otherwise.
   */
  async function loadMachines() {
    let successful = false;

    try {
      isLoadingMachines.value = true;
      const { data } = await callFetchMachinesList();
      isLoadingMachines.value = false;

      if (Array.isArray(data)) {
        machineList.value = data;
        successful = true;
      } else {
        HiNotification.Toast(data.Mensaje || 'No se ha podido obtener la lista de máquinas');
      }
    } catch (error) {
      logError(error);

      HiNotification.Toast('No se ha podido obtener la lista de máquinas');
      isLoadingMachines.value = false;
    }

    return successful;
  }

  watch(opened, async () => {
    if (opened.value) {
      if (!await loadMachines()) {
        opened.value = false;
      }
    } else {
      machineList.value = [];
    }
  });

  return {
    isLoadingMachines,
    machineList,
  };
}

/**
 * @param {object} options
 * @param {Ref<Record<string, any>[]>} options.machineList
 */
function useMachineSelection({ machineList }) {
  /** @type {ShallowRef<InstanceType<typeof UCSearchEntityERP> | null>} */
  const searchMachineRef = shallowRef(null);

  /** @type {Ref<Record<string, any> | null>} */
  const selectedMachine = ref(null);

  /**
   * Handles the selection of a machine.
   * @param {Record<string, any> | null} value - The selected machine object.
   * @returns {void}
   */
  function handleMachineSelection(value) {
    selectedMachine.value = value;
  }

  // Open the next popup once the machine list is loaded
  watch(machineList, async () => {
    if (machineList.value && machineList.value.length > 0) {
      await nextTick();
      searchMachineRef.value?.GetEntity();
    }
  });

  return {
    searchMachineRef,
    selectedMachine,
    handleMachineSelection,
  };
}

/**
 * @param {object} options
 * @param {Ref<Record<string, any> | null>} options.selectedMachine
 */
function useMachineModification({ selectedMachine }) {
  const isModifyingMachine = ref(false);

  /**
   * Updates the selected machine for the repair part.
   * @returns {Promise<void>}
   */
  async function modifyPartMachine() {
    if (!props.workPartId) {
      HiNotification.Toast('No se ha especificado el parte');
      throw new Error('No part provided');
    }

    if (!selectedMachine.value) {
      return;
    }

    try {
      isModifyingMachine.value = true;
      const { data } = await callModifyPartMachine(
        props.workPartId,
        selectedMachine.value.Id_Almacen,
        selectedMachine.value.Id_Articulo,
        selectedMachine.value.NumeroSerie,
      );

      if (data.Valor === true) {
        opened.value = false;
        emit('machineUpdated');

        HiNotification.Toast('La máquina se ha modificado correctamente');
      } else {
        HiNotification.Toast('No se ha podido modificar la máquina');
      }
    } catch (error) {
      logError(error);
      HiNotification.Toast('No se ha podido modificar la máquina');
    } finally {
      isModifyingMachine.value = false;
    }
  }

  /**
   * Handles the confirmation for updating the machine.
   * Displays a confirmation dialog and updates the machine if confirmed.
   *
   * @returns {Promise<void>}
   */
  async function handleModificationConfirmation() {
    const resultConfirm = await ConfirmDialogGH(
      'Esta acción modificará la máquina del parte.',
      '¿Quiere continuar?',
    );

    if (resultConfirm) {
      modifyPartMachine();
    }
  }

  watch(opened, () => {
    if (!opened.value) {
      selectedMachine.value = null;
    }
  });

  return {
    isModifyingMachine,
    handleModificationConfirmation,
  };
}

const {
  isLoadingMachines,
  machineList,
} = useMachineLoading();

const {
  searchMachineRef,
  selectedMachine,
  handleMachineSelection,
} = useMachineSelection({ machineList });

const {
  isModifyingMachine,
  handleModificationConfirmation,
} = useMachineModification({ selectedMachine });
</script>

<style scoped lang="scss">
.change-machine-popup {
  --f7-popup-tablet-height: 275px;

  .v-enter-active,
  .v-leave-active {
    transition: opacity 0.5s ease;
  }

  .v-enter-from,
  .v-leave-to {
    opacity: 0;
  }
}
</style>
